<div class="quote-editor">
    <!-- ITEMS -->
    <section class="quote-section">
        <div class="section-head">
            <h3 class="section-title">{{ 'document.edit.quote.items.title' | translate }}</h3>
            <div class="section-actions">
                <ra-button
                    [label]="'document.edit.quote.catalog.fromCatalog' | translate"
                    [type]="'primary-outline'"
                    [disabled]="service.isBlocked()"
                    (click)="openCatalog('items')"
                />
                <ra-button
                    [label]="'document.edit.quote.items.add' | translate"
                    [type]="'primary'"
                    [disabled]="service.isBlocked()"
                    (click)="openNewItem()"
                />
            </div>
        </div>

        @if (service.items().length === 0) {
            <ra-empty [message]="'document.edit.quote.items.empty' | translate" />
        } @else {
            <div class="quote-table">
                <div class="quote-row quote-row--head">
                    <span class="cell cell--name">{{
                        'document.edit.quote.items.colName' | translate
                    }}</span>
                    <span class="cell">{{
                        'document.edit.quote.items.colUnit' | translate
                    }}</span>
                    <span class="cell cell--num">{{
                        'document.edit.quote.items.colQty' | translate
                    }}</span>
                    <span class="cell cell--num">{{
                        'document.edit.quote.items.colUnitPrice' | translate
                    }}</span>
                    <span class="cell cell--num">{{
                        'document.edit.quote.items.colSubtotal' | translate
                    }}</span>
                    <span class="cell cell--actions">{{
                        'document.edit.quote.items.colActions' | translate
                    }}</span>
                </div>

                @for (item of service.items(); track item._id || $index) {
                    <div class="quote-row">
                        <span class="cell cell--name">{{ item.name }}</span>
                        <span class="cell">{{
                            item.unitOfMeasurement | unitOfMeasurementFormat
                        }}</span>
                        <span class="cell cell--num">{{ item.quantity }}</span>
                        <span class="cell cell--num">{{
                            item.unitPrice | raCurrency: service.currencyCode() : service.locale()
                        }}</span>
                        <span class="cell cell--num">{{
                            lineSubtotal(item) | raCurrency: service.currencyCode() : service.locale()
                        }}</span>
                        <span class="cell cell--actions">
                            @if (!service.isBlocked()) {
                                <ra-square-icon [icon]="'edit'" (click)="openEditItem(item)" />
                                <ra-square-icon
                                    [icon]="'trash'"
                                    [type]="'danger'"
                                    (click)="removeItem(item)"
                                />
                            }
                        </span>
                    </div>
                }
            </div>
        }
    </section>

    <!-- TAXES -->
    <section class="quote-section">
        <div class="section-head">
            <h3 class="section-title">{{ 'document.edit.quote.taxes.title' | translate }}</h3>
            <div class="section-actions">
                <ra-button
                    [label]="'document.edit.quote.catalog.fromCatalog' | translate"
                    [type]="'primary-outline'"
                    [disabled]="service.isBlocked()"
                    (click)="openCatalog('taxes')"
                />
                <ra-button
                    [label]="'document.edit.quote.taxes.add' | translate"
                    [type]="'primary'"
                    [disabled]="service.isBlocked()"
                    (click)="openNewTaxe()"
                />
            </div>
        </div>

        @if (service.taxes().length === 0) {
            <ra-empty [message]="'document.edit.quote.taxes.empty' | translate" />
        } @else {
            <div class="quote-table quote-table--taxes">
                <div class="quote-row quote-row--head">
                    <span class="cell cell--name">{{
                        'document.edit.quote.taxes.colName' | translate
                    }}</span>
                    <span class="cell">{{
                        'document.edit.quote.taxes.colMode' | translate
                    }}</span>
                    <span class="cell cell--num">{{
                        'document.edit.quote.taxes.colValue' | translate
                    }}</span>
                    <span class="cell cell--actions">{{
                        'document.edit.quote.taxes.colActions' | translate
                    }}</span>
                </div>

                @for (taxe of service.taxes(); track taxe._id || $index) {
                    <div class="quote-row">
                        <span class="cell cell--name">{{ taxe.name }}</span>
                        <span class="cell">
                            <span
                                class="mode-tag"
                                [class.mode-tag--percentage]="taxe.mode === 'percentage'"
                            >
                                {{
                                    (taxe.mode === 'percentage'
                                        ? 'document.edit.quote.modePercentage'
                                        : 'document.edit.quote.modeFixed'
                                    ) | translate
                                }}
                            </span>
                        </span>
                        <span class="cell cell--num">
                            @if (taxe.mode === 'percentage') {
                                {{ formatPercent(taxe.price) }}
                            } @else {
                                {{
                                    taxe.price
                                        | raCurrency: service.currencyCode() : service.locale()
                                }}
                            }
                        </span>
                        <span class="cell cell--actions">
                            @if (!service.isBlocked()) {
                                <ra-square-icon [icon]="'edit'" (click)="openEditTaxe(taxe)" />
                                <ra-square-icon
                                    [icon]="'trash'"
                                    [type]="'danger'"
                                    (click)="removeTaxe(taxe)"
                                />
                            }
                        </span>
                    </div>
                }
            </div>
        }
    </section>

    <!-- DISCOUNT -->
    <section class="quote-section">
        <div class="section-head">
            <h3 class="section-title">{{ 'document.edit.quote.discount.title' | translate }}</h3>
            @if (!service.discount()) {
                <ra-button
                    [label]="'document.edit.quote.discount.add' | translate"
                    [type]="'primary'"
                    [disabled]="service.isBlocked()"
                    (click)="openDiscountModal()"
                />
            }
        </div>

        @if (service.discount(); as discount) {
            <div class="quote-row quote-row--single">
                <span class="cell cell--name">
                    @if (discount.mode === 'percentage') {
                        -{{ formatPercent(discount.value) }}
                    } @else {
                        -{{
                            discount.value | raCurrency: service.currencyCode() : service.locale()
                        }}
                    }
                </span>
                <span class="cell cell--actions">
                    @if (!service.isBlocked()) {
                        <ra-square-icon [icon]="'edit'" (click)="openDiscountModal()" />
                        <ra-square-icon
                            [icon]="'trash'"
                            [type]="'danger'"
                            (click)="removeDiscount()"
                        />
                    }
                </span>
            </div>
        } @else {
            <ra-empty [message]="'document.edit.quote.discount.none' | translate" />
        }
    </section>

    <!-- SIGNATURE -->
    <section class="quote-section">
        <div class="section-head">
            <h3 class="section-title">{{ 'document.edit.quote.signature.title' | translate }}</h3>
        </div>
        <div class="signature-card">
            <p class="quote-muted">
                {{
                    (service.isPrivate()
                        ? 'document.edit.quote.signature.enabledDescription'
                        : 'document.edit.quote.signature.disabledDescription'
                    ) | translate
                }}
            </p>
            <ra-button
                [label]="
                    (service.isPrivate()
                        ? 'document.edit.quote.signature.disable'
                        : 'document.edit.quote.signature.enable'
                    ) | translate
                "
                [type]="service.isPrivate() ? 'danger-outline' : 'primary-outline'"
                [disabled]="service.isBlocked()"
                (click)="toggleSignature()"
            />
        </div>
    </section>

    <!-- TOTALS -->
    <section class="quote-totals">
        <div class="total-row">
            <span class="quote-muted">{{ 'document.edit.quote.totals.items' | translate }}</span>
            <span>{{
                service.itemsTotal() | raCurrency: service.currencyCode() : service.locale()
            }}</span>
        </div>

        @if (service.discount(); as discount) {
            <div class="total-row">
                <span class="quote-muted">
                    {{ 'document.edit.quote.totals.discount' | translate }}
                    @if (discount.mode === 'percentage') {
                        ({{ formatPercent(discount.value) }})
                    }
                </span>
                <span>
                    @if (service.discountAmount() !== null) {
                        -{{
                            service.discountAmount()
                                | raCurrency: service.currencyCode() : service.locale()
                        }}
                    } @else {
                        -{{ formatPercent(discount.value) }}
                    }
                </span>
            </div>
        }

        <div class="total-row">
            <span class="quote-muted">{{ 'document.edit.quote.totals.taxes' | translate }}</span>
            <span>{{
                service.taxesTotal() | raCurrency: service.currencyCode() : service.locale()
            }}</span>
        </div>

        <div class="total-row total-row--grand">
            <span>{{ 'document.edit.quote.totals.grandTotal' | translate }}</span>
            <span>{{
                service.grandTotal() | raCurrency: service.currencyCode() : service.locale()
            }}</span>
        </div>
    </section>
</div>

<!-- MODAIS -->
@if (showItemModal()) {
    <modal-quote-item-form
        [item]="itemToEdit()"
        [loading]="loading()"
        [currencyCode]="service.currencyCode()"
        [locale]="service.locale()"
        (close)="closeItemModal()"
        (saveNew)="onItemSaveNew($event)"
        (saveEdit)="onItemSaveEdit($event)"
    />
}

@if (showTaxeModal()) {
    <modal-quote-taxe-form
        [taxe]="taxeToEdit()"
        [loading]="loading()"
        [currencyCode]="service.currencyCode()"
        [locale]="service.locale()"
        (close)="closeTaxeModal()"
        (saveNew)="onTaxeSaveNew($event)"
        (saveEdit)="onTaxeSaveEdit($event)"
    />
}

@if (catalogMode(); as mode) {
    <modal-quote-catalog
        [mode]="mode"
        [loading]="loading()"
        [currencyCode]="service.currencyCode()"
        [locale]="service.locale()"
        (close)="closeCatalog()"
        (addItem)="onCatalogAddItem($event)"
        (addTaxe)="onCatalogAddTaxe($event)"
    />
}

@if (showDiscountModal()) {
    <modal-quote-discount-form
        [discount]="service.discount()"
        [loading]="loading()"
        [currencyCode]="service.currencyCode()"
        [locale]="service.locale()"
        (close)="closeDiscountModal()"
        (save)="onDiscountSave($event)"
    />
}
